/**
 * Shared time constants for `apps/web`.
 *
 * PATTERN TO FOLLOW: any cache, debounce, or timeout duration used in more than
 * one place (or representing a product decision, not a local detail) lives here,
 * not as a loose `const` inside a component.
 *
 * Why: these values were redeclared in ~12 files with two spellings of the same
 * number (`5 * 60 * 1000` and `300_000`), so changing the cache policy required
 * touching every site and risked leaving values divergent. Import from here
 * instead of redefining.
 */

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;

/** Milliseconds in a minute. Base for deriving all other durations. */
const MINUTE_MS = MS_PER_SECOND * SECONDS_PER_MINUTE;

/** Minutes cached data is considered fresh (offline cache and session). */
const STALE_MINUTES = 5;

/**
 * Default `staleTime` for offline-first queries (PowerSync + TanStack).
 *
 * Data syncs locally through PowerSync, so a generous `staleTime` (5 min)
 * avoids redundant refetches/re-renders with no real risk of showing stale data.
 * It is also the global `queryClient` default.
 */
export const OFFLINE_CACHE_STALE_TIME_MS = STALE_MINUTES * MINUTE_MS;

/**
 * `staleTime` for session/authentication queries.
 *
 * It remains a separate constant (although it currently matches the offline
 * cache value) because it is an independent decision: it must stay aligned
 * across `useSession`, the `_app` route guard, and the `session-bridge`.
 */
export const SESSION_STALE_TIME_MS = STALE_MINUTES * MINUTE_MS;

/**
 * Time ceiling for session checks by route guards (`beforeLoad`).
 *
 * P23/CC-60 · I1-bis. Single source for BOTH guards (`_app` and `_auth`):
 * keeping it as a local literal inside `_app` is exactly what left `_auth`
 * without the defense its sibling had—and `/login` was the route that hung.
 * Once the ceiling is reached, the screen is RENDERED; the late response stays
 * alive and syncs the machine and cache when it arrives, and `BootManager`
 * redirects afterward if a session was found. The cost is at most a flicker,
 * never a blank screen.
 */
export const SESSION_CHECK_TIMEOUT_MS = 1500;

/** Seconds before revoking a download's object URL (see constant). */
const OBJECT_URL_REVOKE_SECONDS = 10;

/**
 * Margin before revoking a `blob:` object URL after triggering a download.
 *
 * It must be comfortably greater than the time the browser takes to consume
 * the `<a download>` href; revoking earlier would abort the download.
 */
export const OBJECT_URL_REVOKE_DELAY_MS = OBJECT_URL_REVOKE_SECONDS * MS_PER_SECOND;
